import React, { useState, useEffect } from "react";
import { Search, Plus, ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";

export default function MyProduce() {
  const [produce, setProduce] = useState([]);
  const [filteredProduce, setFilteredProduce] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [producePerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [produceOptions, setProduceOptions] = useState([]);
  const [selectedProduce, setSelectedProduce] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [isAddingProduce, setIsAddingProduce] = useState(false);

  // Fetch my produce data
  useEffect(() => {
    fetchMyProduce();

    const handleSyncComplete = () => {
      fetchMyProduce();
    };

    window.addEventListener('sync-complete', handleSyncComplete);
    window.addEventListener('online', handleSyncComplete);

    return () => {
      window.removeEventListener('sync-complete', handleSyncComplete);
      window.removeEventListener('online', handleSyncComplete);
    };
  }, []);

  // Fetch produce options for modal
  useEffect(() => {
    fetchProduceOptions();
  }, []);

  const fetchMyProduce = async () => {
    try {
      if (produce.length === 0) {
        setLoading(true);
      }

      // Try to get from cache first for immediate display
      const cachedResult = await cacheService.getCachedResponse('/my_produce', 'GET');
      if (cachedResult && cachedResult.status && cachedResult.data && produce.length === 0) {
        const formattedProduce = cachedResult.data.map(item => ({
          id: item.id,
          name: item.name,
          image: item.image,
          harvestDate: item.harvest_date === "null" ? "Not harvested" : item.harvest_date,
          status: "Available",
          category: "Produce"
        }));
        setProduce(formattedProduce);
        setFilteredProduce(formattedProduce);
        setLoading(false);
      }

      const result = await authAPI.getMyProduce();

      if (result.status) {
        const formattedProduce = result.data.map(item => ({
          id: item.id,
          name: item.name,
          image: item.image,
          harvestDate: item.harvest_date === "null" ? "Not harvested" : item.harvest_date,
          status: "Available",
          category: "Produce"
        }));
        setProduce(formattedProduce);
        setFilteredProduce(formattedProduce);
      }
    } catch (error) {
      console.error('Error fetching my produce:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProduceOptions = async () => {
    try {
      const data = await authAPI.getProduce();
      setProduceOptions(data);
    } catch (error) {
      console.error('Error fetching produce options:', error);
    }
  };

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  const categories = ["All", ...new Set(produce.map((item) => item.category))];
  const statuses = ["All", ...new Set(produce.map((item) => item.status))];

  // Filter produce
  useEffect(() => {
    let result = produce;

    if (searchTerm) {
      result = result.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== "All") {
      result = result.filter((item) => item.category === filterCategory);
    }

    if (filterStatus !== "All") {
      result = result.filter((item) => item.status === filterStatus);
    }

    setFilteredProduce(result);
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterStatus, produce]);

  const indexOfLastProduce = currentPage * producePerPage;
  const indexOfFirstProduce = indexOfLastProduce - producePerPage;
  const currentProduce = filteredProduce.slice(
    indexOfFirstProduce,
    indexOfLastProduce
  );
  const totalPages = Math.ceil(filteredProduce.length / producePerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleAddProduce = async () => {
    if (selectedProduce.length === 0) return;

    try {
      setIsAddingProduce(true);
      const result = await authAPI.storeFarmProduce(
        selectedProduce.map(item => item.id)
      );

      // Show the exact message from API
      showToast(result.status ? 'success' : 'error', result.message);

      if (result.status) {
        // Optimistic update if offline or background-synced
        if (result.__offline) {
          const optimisticItems = selectedProduce.map(item => ({
            id: `temp_${Date.now()}_${item.id}`,
            name: item.name,
            image: item.image,
            harvestDate: "Pending sync",
            status: "Pending sync",
            category: "Produce",
            __offline: true
          }));
          setProduce(prev => [...prev, ...optimisticItems]);
          setFilteredProduce(prev => [...prev, ...optimisticItems]);
        }

        // Invalidate related cache to force fresh data on dashboard
        await cacheService.forceInvalidateCache([
          '/manage/farm',
          '/my_produce',
          '/products',
          '/farmer/orders'
        ]);

        // Close modal and reset
        setShowAddModal(false);
        setSelectedProduce([]);
        setModalSearchTerm("");

        // Refresh the produce list from server/cache
        if (!result.__offline) {
          await fetchMyProduce();
        }
      }
    } catch (error) {
      console.error('Error adding produce:', error);
      showToast('error', 'An error occurred while adding produce. Please try again.');
    } finally {
      setIsAddingProduce(false);
    }
  };

  const toggleProduceSelection = (product) => {
    setSelectedProduce(prev => {
      const isSelected = prev.find(item => item.id === product.id);
      if (isSelected) {
        return prev.filter(item => item.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  // Filter products for the modal based on search term and exclude already added produce
  const filteredModalProducts = produceOptions.filter(
    (product) => {
      // First check if it matches the search term
      const matchesSearch = product.name.toLowerCase().includes(modalSearchTerm.toLowerCase());

      // Then check if it's not already in the user's produce
      const isAlreadyAdded = produce.some(userProduce => userProduce.name === product.name);

      return matchesSearch && !isAlreadyAdded;
    }
  );

  if (loading) {
    return (
      <div className="my-produce-container-fxk">
        <div className="my-produce-header-fxk">
          <h1 className="my-produce-title-fxk">My Produce</h1>
        </div>
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  return (
    <div className="my-produce-container-fxk">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
            <button
              className="toast-close-fxk"
              onClick={() => setToast({ show: false, type: '', message: '' })}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="my-produce-header-fxk">
        <h1 className="my-produce-title-fxk">My Produce</h1>
        <button
          className="my-produce-add-btn-fxk"
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={18} /> Add Produce
        </button>
      </div>

      <div className="my-produce-controls-fxk">
        <div className="search-wrapper-app">
          <div className="search-icon-app">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="search-input-app"
            placeholder="Search produce..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="my-produce-filters-fxk">
          <div className="filter-select-wrapper-app">
            <label htmlFor="category-filter" className="filter-label-app">
              Category:
            </label>
            <select
              id="category-filter"
              className="filter-select-app"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-select-wrapper-app">
            <label htmlFor="status-filter" className="filter-label-app">
              Status:
            </label>
            <select
              id="status-filter"
              className="filter-select-app"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="my-produce-grid-fxk">
        {currentProduce.length > 0 ? (
          currentProduce.map((item) => (
            <div key={item.id} className="produce-card-fxk">
              <div className="produce-card-image-container-fxk">
                <img
                  src={item.image}
                  alt={item.name}
                  className="produce-card-image-fxk"
                />
              </div>
              <div className="produce-card-content-fxk">
                <h3 className="produce-card-title-fxk">{item.name}</h3>
                <div className="produce-card-details-fxk">
                  <p className="produce-card-category-fxk">{item.category}</p>
                  <p className="produce-card-date-fxk">
                    Harvested: {item.harvestDate}
                  </p>
                </div>
                <div className="buttons-div-f">
                  <Link
                    to={`/farmer-dashboard/produce/${item.id}`}
                    className="manage-btn"
                  >
                    View
                  </Link>
                  <Link
                    to={`/farmer-dashboard/myfarm/manage/${item.id}`}
                    className="care-btn"
                  >
                    Manage
                  </Link>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-produce-message-fxk">
            No produce found matching your criteria.
          </div>
        )}
      </div>

      {filteredProduce.length > producePerPage && (
        <div className="my-produce-pagination-fxk">
          <button
            className="pagination-btn-fxk"
            onClick={() => paginate(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
            <button
              key={number}
              className={`pagination-btn-fxk ${currentPage === number ? "pagination-active-fxk" : ""
                }`}
              onClick={() => paginate(number)}
            >
              {number}
            </button>
          ))}

          <button
            className="pagination-btn-fxk"
            onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay-fxk">
          <div className="modal-fxk" style={{ maxWidth: "800px" }}>
            <div className="modal-header-fxk">
              <h2 className="modal-title-fxk">Select Produce to Add</h2>
              <button
                className="modal-close-fxk"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProduce([]);
                  setModalSearchTerm("");
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body-fxk">
              {/* Search input for the modal */}
              <div className="modal-search-container-fxk">
                <div className="modal-search-icon-fxk">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  className="modal-search-input-fxk"
                  placeholder="Search products..."
                  value={modalSearchTerm}
                  onChange={(e) => setModalSearchTerm(e.target.value)}
                />
              </div>

              <div className="product-selection-grid-fxk">
                {filteredModalProducts.length > 0 ? (
                  filteredModalProducts.map((product) => {
                    const isSelected = selectedProduce.find(item => item.id === product.id);
                    return (
                      <div
                        key={product.id}
                        className={`product-selection-card-fxk ${isSelected ? "selected-fxk" : ""
                          }`}
                        onClick={() => toggleProduceSelection(product)}
                      >
                        <div className="product-selection-image-container-fxk">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="product-selection-image-fxk"
                          />
                        </div>
                        <div className="product-selection-name-fxk">
                          {product.name}
                        </div>
                        {isSelected && (
                          <div className="selection-indicator-fxk">
                            ✓ Selected
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-products-message-fxk">
                    No products found matching your search.
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer-fxk">
              <button
                className="cancel-btn-fxk"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProduce([]);
                  setModalSearchTerm("");
                }}
                disabled={isAddingProduce}
              >
                Cancel
              </button>
              <button
                className="submit-btn-fxk"
                onClick={handleAddProduce}
                disabled={selectedProduce.length === 0 || isAddingProduce}
              >
                {isAddingProduce ? (
                  <>
                    <Loader2 size={16} className="spinner-fxk" />
                    Adding...
                  </>
                ) : (
                  `Add Selected Produce (${selectedProduce.length})`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        .toast-close-fxk {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close-fxk:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .spinner-fxk {
          animation: spin 1s linear infinite;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}